import React, { useId, useState } from 'react';
import { Search, MapPin } from 'lucide-react';
import DeliveryPointPicker from './DeliveryPointPicker';
import { KIND } from '../lib/deliveryPoints';

/**
 * A centre code field with the HP lookup attached to it.
 *
 * The input stays, because a merchant who ships from the same post office every
 * day knows its number and typing five digits beats opening a dialog. Browse is
 * for everyone else.
 *
 * When a code is picked the point it names is echoed under the field, so the
 * merchant can see they chose the right Zagreb locker before submitting. That
 * line only ever describes the code currently in the field: typing over the
 * code, or the form resetting it, drops the description with it.
 */
export default function DeliveryPointField({
  kind,
  label,
  value,
  onChange,
  help,
  initialSearch = '',
  required = false,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const inputId = useId();

  const chosen = picked && picked.code === value ? picked : null;
  const noun = kind === KIND.LOCKER ? 'parcel locker' : 'post office';

  const handlePicked = (point) => {
    setPicked(point);
    onChange(point.code);
  };

  return (
    <div className="form-group">
      <label className="form-label" htmlFor={inputId}>
        {label}
      </label>

      <div className="input-with-action">
        <input
          id={inputId}
          type="text"
          className="form-control"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={kind === KIND.LOCKER ? 'e.g. 61004' : 'e.g. 10000'}
          required={required}
        />
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setPickerOpen(true)}
          aria-label={`Browse ${noun}s`}
        >
          <Search size={14} /> Browse
        </button>
      </div>

      {chosen ? (
        <p className="field-hint field-hint-chosen">
          <MapPin size={12} style={{ flexShrink: 0 }} />
          <span>
            {chosen.name} — {chosen.address}, {chosen.zip} {chosen.city}
          </span>
        </p>
      ) : (
        help && <p className="field-hint">{help}</p>
      )}

      <DeliveryPointPicker
        isOpen={pickerOpen}
        kind={kind}
        selectedCode={value}
        initialSearch={initialSearch}
        onSelect={handlePicked}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}
